import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Modal,
    Box,
    CircularProgress,
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { useCurrency } from 'context/CurrencyContext';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const OrderCancellationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canUpdate, canDelete } = usePermission('order_cancellations');
    const { formatCurrency, currency: systemCurrency } = useCurrency();

    const [cancellation, setCancellation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchCancellationData();
    }, [id]);

    const fetchCancellationData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/order-cancellations/${id}`);
            setCancellation(response.data);
        } catch (error) {
            console.error('Error fetching order cancellation data:', error);
            setSnackbar({ open: true, message: 'Error loading order cancellation details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setCancellation(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/order-cancellations/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Order Cancellation updated successfully', severity: 'success' });
            fetchCancellationData();
        } catch (error) {
            console.error('Error updating order cancellation:', error);
            setSnackbar({ open: true, message: 'Error updating order cancellation', severity: 'error' });
        }
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleDeleteCancel = () => {
        if (isDeleting) return;
        setDeleteModalOpen(false);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await axiosInstance.delete(`/order-cancellations/${id}`);
            setSnackbar({ open: true, message: 'Order Cancellation deleted successfully', severity: 'success' });
            setTimeout(() => navigate('/order-cancellations'), 800);
        } catch (error) {
            console.error('Error deleting order cancellation:', error);
            setSnackbar({ open: true, message: 'Error deleting order cancellation', severity: 'error' });
            setIsDeleting(false);
            setDeleteModalOpen(false);
        }
    };

    const renderHyperlink = (text, onClick) => {
        return (
            <MDTypography
                component="span"
                onClick={onClick}
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                {text}
            </MDTypography>
        );
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={cancellation?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!cancellation) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Order Cancellation not found</MDTypography>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {cancellation.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Order Cancellation Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchCancellationData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        canUpdate && (
                                            <Tooltip title="Edit Order Cancellation">
                                                <IconButton
                                                    onClick={() => setIsEditing(true)}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Edit sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        )
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Delete Order Cancellation">
                                            <IconButton
                                                onClick={handleDeleteClick}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Delete sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Cancellation Information", [
                                    {
                                        label: "Cancellation Name",
                                        field: "name"
                                    },
                                    {
                                        label: "Travel Order",
                                        field: "travel_order_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const toId = cancellation.travel_order_id;
                                            const toName = cancellation.travel_order?.name || (toId ? `TO-${toId}` : null);
                                            if (!toId) return '-';
                                            return renderHyperlink(
                                                toName,
                                                () => navigate(`/travel-order/${toId}`)
                                            );
                                        }
                                    },
                                    {
                                        label: "Invoice",
                                        field: "invoice_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const invId = cancellation.invoice_id;
                                            const invName = cancellation.invoice?.invoice_number || cancellation.invoice?.name || (invId ? `Invoice #${invId}` : null);
                                            if (!invId) return '-';
                                            return renderHyperlink(
                                                invName,
                                                () => navigate(`/invoices/${invId}`)
                                            );
                                        }
                                    },
                                    {
                                        label: "Order Line Item",
                                        field: "order_line_item_id",
                                        readOnly: true,
                                        renderValue: (val) => {
                                            const itemName = cancellation.order_line_item?.name || (val ? `Item #${val}` : '-');
                                            if (cancellation.travel_order_id) {
                                                return renderHyperlink(
                                                    itemName,
                                                    () => navigate(`/travel-order/${cancellation.travel_order_id}`)
                                                );
                                            }
                                            return itemName;
                                        }
                                    },
                                    {
                                        label: "Primary Pax",
                                        field: "order_pax_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const paxId = cancellation.order_pax_id;
                                            const paxName = cancellation.order_pax?.passenger_name || (paxId ? `Pax #${paxId}` : cancellation.pax_name);
                                            if (!paxId) return paxName || '-';
                                            return renderHyperlink(
                                                paxName,
                                                () => navigate(`/travel-order-pax/${paxId}`)
                                            );
                                        }
                                    },
                                    {
                                        label: "Cancellation Type",
                                        field: "cancellation_type",
                                        options: [
                                            { value: "Departure Date Change", label: "Departure Date Change" },
                                            { value: "Min. Passenger Not Met", label: "Min. Passenger Not Met" },
                                            { value: "Customer Cancellation", label: "Customer Cancellation" },
                                            { value: "Non Payment", label: "Non Payment" },
                                            { value: "Circumstantial", label: "Circumstantial" }
                                        ]
                                    },
                                    {
                                        label: "Requested On",
                                        field: "requested_on",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime')
                                    },
                                    {
                                        label: "Status",
                                        field: "status",
                                        options: [
                                            { value: "Active", label: "Active" },
                                            { value: "Not Submitted", label: "Not Submitted" },
                                            { value: "Submitted", label: "Submitted" },
                                            { value: "Approved", label: "Approved" },
                                            { value: "Rejected", label: "Rejected" }
                                        ]
                                    },
                                ])}

                                {renderSection("Refund Information", [
                                    {
                                        label: "Refund Type",
                                        field: "refund_type",
                                        options: [
                                            { value: "Coupon", label: "Coupon" },
                                            { value: "Direct Refund", label: "Direct Refund" },
                                            { value: "No Refund", label: "No Refund" }
                                        ],
                                        renderValue: (val) => val || (cancellation.coupon_id ? "Coupon" : (cancellation.refund_amount > 0 ? "Direct Refund" : (cancellation.refund_status === 'NA' ? 'No Refund' : '-')))
                                    },
                                    {
                                        label: "Cancellation Status",
                                        field: "refund_status",
                                        options: [
                                            { value: "Initiated", label: "Cancellation Initiated" },
                                            { value: "In-Progress", label: "In-Progress" },
                                            { value: "Processing", label: "In-Progress" },
                                            { value: "Refunded", label: "Refunded" },
                                            { value: "Failed", label: "Failed" },
                                            { value: "NA", label: "NA" },
                                        ],
                                        renderValue: (val) => {
                                            const map = {
                                                'Initiated': 'Cancellation Initiated',
                                                'In-Progress': 'In-Progress',
                                                'Processing': 'In-Progress',
                                                'Refunded': 'Refunded',
                                                'Failed': 'Failed',
                                                'NA': 'NA'
                                            };
                                            return map[val] || val || '-';
                                        }
                                    },
                                    {
                                        label: "Total Amount",
                                        field: "total_amount",
                                        renderValue: (val) => val != null ? formatCurrency(val, cancellation?.currency || cancellation?.travel_order?.currency || systemCurrency || 'USD') : '-'
                                    },
                                    {
                                        label: "Amount Deduction",
                                        field: "amount_deduction",
                                        renderValue: (val) => val != null ? formatCurrency(val, cancellation?.currency || cancellation?.travel_order?.currency || systemCurrency || 'USD') : '-'
                                    },
                                    {
                                        label: "Refund Amount",
                                        field: "refund_amount",
                                        renderValue: (val) => val != null ? formatCurrency(val, cancellation?.currency || cancellation?.travel_order?.currency || systemCurrency || 'USD') : '-'
                                    },
                                    {
                                        label: "Coupon",
                                        field: "coupon_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const couponId = cancellation.coupon_id;
                                            if (!couponId) return '-';
                                            const couponName = cancellation.coupon?.name || cancellation.coupon?.code || `Coupon #${couponId}`;
                                            return renderHyperlink(
                                                couponName,
                                                () => navigate(`/coupons/${couponId}`)
                                            );
                                        }
                                    },
                                ])}

                                {renderSection("Additional Information", [
                                    { label: "Reject Reason", field: "reject_reason" },
                                    { label: "Comments", field: "comments" },
                                ])}

                                {renderSection("Audit Information", [
                                    {
                                        label: "Created By",
                                        field: "created_by_name",
                                        readOnly: true,
                                        renderValue: (val) => cancellation.created_by ? renderHyperlink(
                                            val || `User #${cancellation.created_by}`,
                                            () => navigate(`/users/${cancellation.created_by}/profile`)
                                        ) : (val || '-')
                                    },
                                    {
                                        label: "Modified By",
                                        field: "updated_by_name",
                                        readOnly: true,
                                        renderValue: (val) => cancellation.updated_by ? renderHyperlink(
                                            val || `User #${cancellation.updated_by}`,
                                            () => navigate(`/users/${cancellation.updated_by}/profile`)
                                        ) : (val || '-')
                                    },
                                    {
                                        label: "Created Date",
                                        field: "created_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime')
                                    },
                                    {
                                        label: "Modified Date",
                                        field: "updated_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime')
                                    },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Modal
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-order-cancellation-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 450 },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <MDTypography variant="h6" component="h2" mb={2}>
                        Delete Order Cancellation
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        Are you sure you want to delete order cancellation <strong>{cancellation.name}</strong>? This action cannot be undone.
                    </MDTypography>
                    <MDBox display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton
                            onClick={handleDeleteCancel}
                            variant="outlined"
                            color="secondary"
                            disabled={isDeleting}
                        >
                            Cancel
                        </MDButton>
                        <MDButton
                            onClick={handleDeleteConfirm}
                            variant="gradient"
                            color="error"
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Delete'
                            )}
                        </MDButton>
                    </MDBox>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderCancellationDetail;
